import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ModalService } from '../../services/modal.service';
import { ModalStates } from '../../models/modal';

@Component({
  selector: 'lib-multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class MultiselectComponent implements OnInit, OnChanges, ControlValueAccessor {
  value: any[] = [];
  disabled: boolean;
  @Input()
  options: Option[] = [];
  @Input()
  maxSelections: number = null;
  @Input()
  defaultButtonText: string = 'Select...';
  @Input()
  allowSelectAll: boolean = false;
  public selectAll = false;
  public opts: MultiSelectOption[];

  onChange: any = () => {};

  constructor(private modalService: ModalService) {}

  get buttonText() {
    const selected = this.opts.filter(x => x.selected);
    if (selected.length > 0) {
      return `${selected.length} Selected`;
    }
    return this.defaultButtonText;
  }
  get selectedCount() {
    return this.opts.filter(x => x.selected).length;
  }

  ngOnInit(): void {
    this._setOptions(this.options);
  }
  ngOnChanges(changes: SimpleChanges) {
    this._setOptions(changes.options.currentValue);
  }

  disableCheckbox(val: any) {
    if (this.disabled) {
      return true;
    }
    if (this.maxSelections != null) {
      return (
        this.maxSelections == this.selectedCount && !this.opts.find(x => x.value === val).selected
      );
    }
    return false;
  }
  selectAllChanged(val: boolean) {
    this.selectAll = val;
    const maxSelected = this.maxSelections ?? this.opts.length;
    for (let i = 0; i < maxSelected; i++) {
      this.opts[i].selected = val;
    }
    this.value = this.opts.filter(x => x.selected).map(x => x.value);
    this.onChange(this.value);
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any[]): void {
    if (obj == null) {
      this.value = [];
      return;
    }
    this.value = obj;
    this.opts.forEach(opt => {
      opt.selected = this.value.some(v => v === opt.value);
    });
    if (this.allowSelectAll) {
      const selected = this.opts.filter(x => x.selected);
      if (selected.length === this.maxSelections || selected.length === this.opts.length) {
        this.selectAll = true;
      } else {
        this.selectAll = false;
      }
    }
  }

  valueChanged(selected: boolean, val: any) {
    if (this.maxSelections != null && selected) {
      if (this.opts.filter(x => x.selected).length === this.maxSelections) {
        this.modalService.showAlertModal(
          `You may only select ${this.maxSelections} options.`,
          ModalStates.Warning
        );
        return;
      }
    }
    const opt = this.opts.find(x => x.value === val);
    opt.selected = selected;
    this.value = this.opts.filter(x => x.selected).map(x => x.value);
    if (this.allowSelectAll) {
      const selected = this.opts.filter(x => x.selected);
      if (selected.length === this.maxSelections || selected.length === this.opts.length) {
        this.selectAll = true;
      } else {
        this.selectAll = false;
      }
    }
    this.onChange(this.value);
  }
  private _setOptions(opts: Option[]) {
    this.opts = this.options.map(o => {
      return {
        value: o.value,
        text: o.text,
        selected: this.value.some(v => v === o.value)
      };
    });
  }
}
export interface Option {
  value: any;
  text: any;
}
export interface MultiSelectOption {
  value: any;
  text: string;
  selected: boolean;
}
